import React from 'react';
import { WorkspaceStatusPill } from '../workflow/WorkflowWorkspacePrimitives';

export type MappingStatusChipState =
  | 'mapped'
  | 'unmapped'
  | 'required'
  | 'changed'
  | 'overridden'
  | 'seeded'
  | 'missing'
  | 'optional'
  | 'cleared'
  | 'default';

export interface MappingStatusChipProps {
  state?: MappingStatusChipState;
  label?: string;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'error' | 'info';
  size?: 'default' | 'compact';
  uppercase?: boolean;
  dataTestId?: string;
}

const stateToneMap: Record<MappingStatusChipState, 'neutral' | 'accent' | 'success' | 'warning' | 'error' | 'info'> = {
  mapped: 'success',
  unmapped: 'error',
  required: 'warning',
  changed: 'info',
  overridden: 'info',
  seeded: 'success',
  missing: 'warning',
  optional: 'neutral',
  cleared: 'warning',
  default: 'neutral',
};

const defaultLabelMap: Record<MappingStatusChipState, string> = {
  mapped: 'Mapped',
  unmapped: 'Unmapped',
  required: 'Required',
  changed: 'Changed',
  overridden: 'Overridden',
  seeded: 'Seeded',
  missing: 'Missing',
  optional: 'Optional',
  cleared: 'Cleared',
  default: 'Default',
};

/**
 * Mapping-specific status chip.
 *
 * Wraps the shared WorkspaceStatusPill and maps mapping-domain states
 * to the workspace tone palette so colors stay consistent across the
 * table, inspector, and navigator.
 *
 * Can also be driven directly by `tone` and `label` for summary badges.
 */
export const MappingStatusChip: React.FC<MappingStatusChipProps> = ({
  state,
  label,
  tone,
  size = 'compact',
  uppercase = false,
  dataTestId,
}) => {
  const resolvedTone = tone ?? (state ? stateToneMap[state] : 'neutral');
  const resolvedLabel = label ?? (state ? defaultLabelMap[state] : '');

  return (
    <WorkspaceStatusPill
      label={resolvedLabel}
      tone={resolvedTone}
      size={size}
      uppercase={uppercase}
      dataTestId={dataTestId}
    />
  );
};

export default MappingStatusChip;
